import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Divider } from '@mui/material';
import { Grid, KeyValue, Pill, SectionHeader, Typography } from 'web-library';
import { Theme as AugmentedTheme } from '@mui/material/styles';

import { makeStyles } from 'tss-react/mui';
import { useParams } from 'react-router-dom';
import { stringToLongDateAndShortTime } from '../../../utils/DateUtils';
import { ExamRegistrationType } from '../../../api/candidate/exam/model';
import DownloadSummaryPDFButton from '../../../components/buttons/DownloadSummaryPDFButton';
import { usePostGroupExamRegistrationSummaryPDF } from '../../../api-refactored/group-exam-registration/usePostGroupExamRegistrationSummaryPDF';
import { useGetDocumentUploadStatus } from '../../../api-refactored/document/useGetDocumentUploadStatus';
import { FileUploadState } from '../../../models/File';
import DownloadProgressModal from '../../../components/modals/DownloadProgressModal';

const useStyles = makeStyles()((theme) =>
({
  divider: {
    marginTop: theme.spacing(6),
    marginBottom: theme.spacing(6),
  },
  statusContainer: {
    display: 'flex',
  },
  statusPill: {
    paddingRight: theme.spacing(2),
    [theme.breakpoints.down('md')]: {
      paddingRight: theme.spacing(1),
      marginTop: theme.spacing(1),
    },
  },
  registrationType: {
    [theme.breakpoints.down('md')]: { alignSelf: 'flex-end' },
    fontWeight: 700,
    color: theme.color.state.stateInfo,
  },
}));

const openDownloadedUrl = (url: string) => {
  const newWindow = window.open(url, '_blank', 'noopener,noreferrer');
  if (newWindow) {
    newWindow.opener = null;
  }
};

interface ISummaryBaseInfo {
  applicationId: string;
  coordinatorName: string;
  examYear: number;
  examLevel: string[];
  applicationStatus: string;
  applicationDateTime: string;
  examRegistrationType: ExamRegistrationType;
}

const SummaryBaseInfo = ({
  applicationId,
  coordinatorName,
  examYear,
  examLevel,
  applicationStatus,
  applicationDateTime,
  examRegistrationType,
}: ISummaryBaseInfo) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const { uuid: applicationUuid } = useParams<any>();
  const [fileUploadStatus, setFileUploadStatus] = useState(FileUploadState.IDLE);
  const [fileUrl, setFileUrl] = useState('');
  const {
    mutate,
    data: mutateSummaryPDF,
    isLoading: isLoadingTriggerSummary,
    isSuccess: isSuccessTriggerSummary,
    reset: resetTriggerSummary,
  } = usePostGroupExamRegistrationSummaryPDF({});
  const fileIsUploading = fileUploadStatus === FileUploadState.UPLOADING;
  useGetDocumentUploadStatus({
    fileKey: mutateSummaryPDF?.uuid || '',
    enabled:
      !!mutateSummaryPDF?.uuid &&
      (fileUploadStatus === FileUploadState.IDLE || fileUploadStatus === FileUploadState.UPLOADING),
    refetchInterval: 5000,
    onSuccess: (response) => {
      if (response.fileStatus === FileUploadState.UPLOADING) {
        setFileUploadStatus(FileUploadState.UPLOADING);
      }
      if (response.fileStatus === FileUploadState.SUCCESS && response.url) {
        setFileUploadStatus(FileUploadState.SUCCESS);
        resetTriggerSummary();
        setFileUrl(response.url);
        openDownloadedUrl(response.url);
      }
      if (response.fileStatus === FileUploadState.FAILED) {
        setFileUploadStatus(FileUploadState.FAILED);
        resetTriggerSummary();
      }
    },
  });

  const handleCloseDownloadErrorPrompt = () => {
    setFileUploadStatus(FileUploadState.IDLE);
  };

  const getRegistrationType = () => {
    if (
      examRegistrationType === ExamRegistrationType.SUPPLEMENTARY ||
      examRegistrationType === ExamRegistrationType.LATE_SUPPLEMENTARY
    ) {
      return t('exam->>SupplementaryRegistration.SupplementaryRegistration');
    }
    return '';
  };
  return (
    <>
      <SectionHeader
        title={t('exam->>RegistrationSummary.RegistrationSummary')}
      >
        {DownloadSummaryPDFButton({
          isDownloading: isLoadingTriggerSummary || isSuccessTriggerSummary || fileIsUploading,
          onDownloadAction: () => {
            if (fileUrl) {
              openDownloadedUrl(fileUrl);
            } else {
              mutate({ applicationUuid });
            }
          },
        })}
      </SectionHeader>
      <Grid container direction="row">
        <Grid md={6} sm={12} xs={12}>
          <KeyValue
            keyText={t('exam->>RegistrationSummary.ApplicationId')}
            valueText={applicationId}
          />
        </Grid>
        <Grid md={6} sm={12} xs={12}>
          <KeyValue
            keyText={t('exam->>RegistrationSummary.Status')}
            valueText={
              <div className={classes.statusContainer}>
                <Grid className={classes.statusPill}>
                  <Pill label={applicationStatus} variant="positive" />
                </Grid>
                <Typography className={classes.registrationType}>
                  {getRegistrationType()}
                </Typography>
              </div>
            }
          />
        </Grid>
      </Grid>
      <Grid container direction="row">
        <Grid md={6} sm={12}>
          <KeyValue
            keyText={t('exam->>RegistrationSummary.ApplicationDateTime')}
            valueText={stringToLongDateAndShortTime(applicationDateTime)}
          />
        </Grid>
      </Grid>
      <Grid container direction="row">
        <Grid md={6} sm={12}>
          <KeyValue
            keyText={t('exam->>RegistrationSummary.Coordinator')}
            valueText={coordinatorName}
          />
        </Grid>
      </Grid>

      <Divider className={classes.divider} />
      <Grid container direction="row">
        <Grid md={6} sm={12} xs={12}>
          <KeyValue
            id="examYear"
            keyText={t('exam->>RegistrationSummary.ExamYear')}
            valueText={examYear.toString()}
          />
        </Grid>
        <Grid md={6} sm={12} xs={12}>
          <KeyValue
            id="examLevel"
            keyText={t('exam->>RegistrationSummary.ExamLevel')}
            valueText={examLevel.join(', ')}
          />
        </Grid>
      </Grid>
      {fileUploadStatus === FileUploadState.FAILED && (
        <DownloadProgressModal
          modalState
          fileDownloadStatus={FileUploadState.FAILED}
          onDialogClose={handleCloseDownloadErrorPrompt}
        />
      )}
    </>
  );
};

export default SummaryBaseInfo;
